/** Change history panel for one backend object (generic objchange rows).
 *
 * Mountable as a detail-page tab or a dialog tab: renders the paginated
 * change list (who / when / what / source / reason), expandable per-row
 * field-level before/after detail, and — when the row is restorable and the
 * viewer holds `console_changes_restore` — the restore flow.
 *
 * `contentType` is the backend `app_label.model` label (e.g.
 * "demo.launch_policy"). Aggregate mode (default): child-model rows grouped
 * under this object are included, and rows sharing a `correlation_id` are
 * grouped client-side into one expandable "edit event" — one tour save
 * touching six models reads as one event, not six scattered rows.
 *
 * `also` merges a second objchange root into the same list (`/changes/?also=`, one
 * list in time order — the itinerary record's group and its default itinerary,
 * plan O6): a Record column then names the root each row (and each edit event's
 * rows) belongs to — after Changed, before the free-text Reason. A 403 says which permission is missing (`forbiddenText`); any
 * other failure gives the server's words and Retry.
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import LinkIcon from '@mui/icons-material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import * as changesApi from '@/api/changes';
import { ConsoleApiError } from '@/api/client';
import type { ChangeAction, ObjectChange } from '@/api/changes';
import { HelpLink } from '@/components/HelpLink';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate, formatRelativeTime } from '@/utils/format';
import {
  apiRequestsHref,
  auditHref,
  changesHref,
  cond,
  createdAroundNode,
} from '@/utils/gridLinks';

import { ChangeSetChip } from './ChangeSetChip';
import { RestoreDialog } from './RestoreDialog';

/** The run/request ids every change row carries but never showed: the
 *  background task, the import run, the originating HTTP request. The
 *  api-requests search is offered only for API- and integration-sourced
 *  changes — that grid stores webhook/API payloads, so a console PATCH's
 *  request id would just search to an empty page. */
function traceActions(row: ObjectChange): ValueAction[] {
  const out: ValueAction[] = [];
  if (row.task_id) {
    out.push(
      act.open(`/bgtasks/${row.task_id}`, 'Open the background task'),
      act.filter(changesHref({ task_id: row.task_id }), 'Every change by this task'),
      act.filter(auditHref({ task_id: row.task_id }), 'Audit trail of this task'),
    );
  }
  if (row.import_run_id) {
    out.push(act.open(`/import-runs/${row.import_run_id}`, 'Open the import run'));
  }
  if (row.root_request_id) {
    if (row.source === 'api' || row.source === 'integration') {
      out.push(
        act.filter(
          apiRequestsHref([cond('request', row.root_request_id, 'exact')], {
            window: createdAroundNode(row.created_ts),
          }),
          'The API request that made this change (±1 h)',
        ),
      );
    }
    out.push(act.copy(row.root_request_id, 'Copy request id'));
  }
  if (row.correlation_id) out.push(act.copy(row.correlation_id, 'Copy correlation id'));
  return out;
}

const ACTION_COLOR: Record<ChangeAction, 'success' | 'info' | 'error' | 'warning'> = {
  created: 'success',
  updated: 'info',
  deleted: 'error',
  restored: 'warning',
};

const SOURCE_LABEL: Record<string, string> = {
  console: 'Console',
  dashboard: 'Dashboard',
  admin: 'Django admin',
  api: 'API',
  integration: 'Integration',
  management: 'Management cmd',
  celery: 'Celery',
  signal: 'Signal',
  system: 'System',
  mcp: 'MCP',
  chat: 'Console agent',
  translation_sync: 'Translation sync',
  revert: 'Revert',
};

const MAX_PATH_CHIPS = 6;
const MAX_EVENT_CHIPS = 4;
const PAGE_SIZE = 25;

/** A second objchange root merged into the panel's (`also=`), and what its rows are called. */
export interface HistoryAlsoRoot {
  /** `app_label.model`, e.g. "demo.itinerary". */
  contentType: string;
  objectPk: string;
  /** "Catalog itinerary". */
  label: string;
}

/** The objchange root a row files under: its aggregate root for a child row, else itself. */
export function rowRoot(row: Pick<ObjectChange, 'content_type' | 'object_pk' | 'aggregate_type' | 'aggregate_pk'>): string {
  return `${row.aggregate_type ?? row.content_type}:${row.aggregate_pk ?? row.object_pk}`;
}

/** The words of the 403 without `also`. */
export const HISTORY_FORBIDDEN = 'You can’t see this history: it needs the Audit view permission (console_audit_view).';

function ValueCell({
  value,
  displayMap,
}: {
  value: unknown;
  displayMap?: Record<string, string> | undefined;
}) {
  if (value === undefined) {
    return (
      <Typography component="span" variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  let text =
    value === null
      ? 'null'
      : typeof value === 'object'
        ? JSON.stringify(value, null, 1)
        : String(value);
  // FK ids resolve to reprs at read time (backend `display` map).
  if (value !== null && typeof value !== 'object') {
    const label = displayMap?.[String(value)];
    if (label && label !== String(value)) text = `${label} (#${String(value)})`;
  }
  return (
    <Typography
      component="pre"
      variant="body2"
      sx={{
        m: 0,
        fontFamily: 'monospace',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: 160,
        overflow: 'auto',
      }}
    >
      {text}
    </Typography>
  );
}

/** Expanded row body: fetches the detail (snapshots) lazily. */
function ChangeDetail({
  changeId,
  onRestored,
}: {
  changeId: number;
  onRestored?: (() => void) | undefined;
}) {
  const { hasPerm } = usePermissions();
  const [restoreOpen, setRestoreOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['objectChanges', 'detail', changeId],
    queryFn: () => changesApi.retrieve(changeId),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (error || !data) return <Alert severity="error">Couldn’t load change detail.</Alert>;

  const before = data.state_before;
  const after = data.state_after;
  if (before === null && after === null) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        Field values are not stored for {SOURCE_LABEL[data.source] ?? data.source} changes — only
        the list of changed fields ({data.changed_paths.join(', ') || 'none'}).
      </Alert>
    );
  }

  const fields = Array.from(
    new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]),
  ).sort();
  const canRestore = data.restorable_fields.length > 0 && hasPerm('console_changes_restore');

  return (
    <Box sx={{ py: 1 }}>
      <Table size="small" sx={{ '& td, & th': { verticalAlign: 'top' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '24%' }}>Field</TableCell>
            <TableCell sx={{ width: '38%' }}>Before</TableCell>
            <TableCell sx={{ width: '38%' }}>After</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f) => (
            <TableRow key={f}>
              <TableCell>
                <code>{f}</code>
              </TableCell>
              <TableCell>
                <ValueCell value={before?.[f]} displayMap={data.display?.[f]} />
              </TableCell>
              <TableCell>
                <ValueCell value={after?.[f]} displayMap={data.display?.[f]} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {canRestore && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setRestoreOpen(true)}
            color="warning"
            variant="outlined"
          >
            Restore previous values…
          </Button>
        </Box>
      )}
      <RestoreDialog
        open={restoreOpen}
        change={data}
        onClose={() => setRestoreOpen(false)}
        onRestored={onRestored}
      />
    </Box>
  );
}

/** One change row + its collapsible detail row. */
function ChangeRow({
  row,
  expanded,
  onToggle,
  onRestored,
  nested = false,
  rootLabel,
}: {
  row: ObjectChange;
  expanded: boolean;
  onToggle: () => void;
  onRestored: () => void;
  nested?: boolean;
  /** With `also`: the root this row files under, in words (the Record column, before the
   *  free-text Reason). */
  rootLabel?: string | undefined;
}) {
  const trace = traceActions(row);
  return (
    <Fragment>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& td': { borderBottom: expanded ? 0 : undefined } }}
        onClick={onToggle}
      >
        <TableCell padding="none" align="center" sx={nested ? { pl: 2 } : undefined}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </TableCell>
        <TableCell sx={nested ? { pl: 4 } : undefined}>
          <Tooltip title={formatDate(row.created_ts)}>
            <span>{formatRelativeTime(row.created_ts)}</span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip size="small" color={ACTION_COLOR[row.action]} label={row.action} />
          {row.aggregate_type && (
            <Tooltip title={`Change to a nested ${row.content_type} record`}>
              <Chip
                size="small"
                variant="outlined"
                sx={{ ml: 0.5 }}
                label={row.object_repr || row.content_type}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          {row.actor?.id ? (
            <ValueActions
              actions={[
                act.open(`/users/${row.actor.id}`, 'Open user'),
                act.copy(row.actor.email, 'Copy email'),
              ]}
            >
              {row.actor.email}
            </ValueActions>
          ) : (
            (row.actor?.email ?? <em>system</em>)
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title={[row.ip, row.user_agent].filter(Boolean).join(' · ')}>
            <span>{SOURCE_LABEL[row.source] ?? row.source}</span>
          </Tooltip>
          {trace.length > 0 && (
            <ValueActions actions={trace} sx={{ ml: 0.5, textDecoration: 'none' }}>
              <LinkIcon
                titleAccess="Trace: task, import run, request"
                sx={{ fontSize: 14, verticalAlign: 'middle', color: 'text.secondary' }}
              />
            </ValueActions>
          )}
          <ChangeSetChip changeSet={row.change_set} sx={{ ml: 0.5 }} />
        </TableCell>
        <TableCell>
          {row.changed_paths.slice(0, MAX_PATH_CHIPS).map((p) => (
            <Chip key={p} size="small" variant="outlined" label={p} sx={{ mr: 0.5, mb: 0.25 }} />
          ))}
          {row.changed_paths.length > MAX_PATH_CHIPS && (
            <Chip
              size="small"
              label={`+${row.changed_paths.length - MAX_PATH_CHIPS}`}
              sx={{ mr: 0.5, mb: 0.25 }}
            />
          )}
        </TableCell>
        {rootLabel !== undefined && (
          <TableCell sx={{ whiteSpace: 'nowrap' }} data-history-root>
            {rootLabel}
          </TableCell>
        )}
        <TableCell sx={{ maxWidth: 220 }}>
          <Typography variant="body2" noWrap title={row.comment}>
            {row.comment || '—'}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={rootLabel !== undefined ? 8 : 7} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <ChangeDetail changeId={row.id} onRestored={onRestored} />
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

interface EventGroup {
  key: string;
  rows: ObjectChange[];
}

/** Group the page's rows into edit events by correlation_id (one request /
 * task = one event). Rows without a correlation stay standalone. Page order
 * (-created_ts) is preserved via first appearance. */
function groupByCorrelation(rows: ObjectChange[]): EventGroup[] {
  const groups: EventGroup[] = [];
  const byCorrelation = new Map<string, EventGroup>();
  for (const row of rows) {
    const cid = row.correlation_id;
    if (!cid) {
      groups.push({ key: `row-${row.id}`, rows: [row] });
      continue;
    }
    const existing = byCorrelation.get(cid);
    if (existing) {
      existing.rows.push(row);
    } else {
      const group = { key: cid, rows: [row] };
      byCorrelation.set(cid, group);
      groups.push(group);
    }
  }
  return groups;
}

export function HistoryPanel({
  contentType,
  objectPk,
  aggregate = true,
  also = null,
  rootLabel = 'This record',
  forbiddenText = HISTORY_FORBIDDEN,
  onRestored,
}: {
  contentType: string;
  objectPk: string;
  /** Include child-model rows grouped under this object (default true). */
  aggregate?: boolean;
  /** A second root merged in (`also=`); null or absent: this object's history alone. */
  also?: HistoryAlsoRoot | null;
  /** With `also`: what this object's own rows are called in the Record column. */
  rootLabel?: string;
  /** What a 403 says: the permissions this history needs. */
  forbiddenText?: string;
  /** Called after a successful restore — refetch the record being edited. */
  onRestored?: () => void;
}) {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set());
  const alsoParam = also ? `${also.contentType}:${also.objectPk}` : undefined;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['objectChanges', 'list', contentType, objectPk, aggregate, alsoParam ?? null, page],
    queryFn: () =>
      changesApi.list({
        content_type: contentType,
        object_pk: objectPk,
        aggregate,
        ...(alsoParam ? { also: alsoParam } : {}),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });
  // With `also`: the root each row files under, in words.
  const labelOf = (row: ObjectChange): string | undefined =>
    also ? (rowRoot(row) === alsoParam ? also.label : rootLabel) : undefined;

  const restored = () => {
    void qc.invalidateQueries({ queryKey: ['objectChanges'] });
    onRestored?.();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error instanceof ConsoleApiError && error.status === 403) {
    return (
      <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />} data-history-forbidden>
        {forbiddenText}
      </Alert>
    );
  }
  if (error || !data) {
    return (
      <Alert
        severity="error"
        data-history-error
        action={
          <Button color="inherit" size="small" disabled={isFetching} onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        Couldn’t load change history.
        {error instanceof ConsoleApiError && error.message ? ` ${error.message}` : ''}
      </Alert>
    );
  }
  if (!data.results.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        No recorded changes yet. History accrues from the moment tracking was enabled for this
        record type. <HelpLink story="ui-change-history" newTab />
      </Typography>
    );
  }

  const groups = groupByCorrelation(data.results);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setExpanded(null);
  };

  return (
    <Box>
      {/* The table scrolls sideways in its own region (overflow-x: auto): at 390 px its seven or
          eight columns are wider than the page, and the page itself never scrolls sideways.
          The pager stays outside it, in view. */}
      <TableContainer data-history-scroll>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36 }} />
              <TableCell>When</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Via</TableCell>
              <TableCell>Changed</TableCell>
              {also && <TableCell>Record</TableCell>}
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              if (group.rows.length === 1) {
                const row = group.rows[0]!;
                return (
                  <ChangeRow
                    key={group.key}
                    row={row}
                    expanded={expanded === row.id}
                    onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                    onRestored={restored}
                    rootLabel={labelOf(row)}
                  />
                );
              }
              const first = group.rows[0]!;
              // One correlation id, so one change set for the whole event.
              const changeSet = group.rows.find((r) => r.change_set)?.change_set;
              const open = openGroups.has(group.key);
              const objects = Array.from(
                new Set(group.rows.map((r) => r.object_repr || r.content_type)),
              );
              return (
                <Fragment key={group.key}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}
                    onClick={() => toggleGroup(group.key)}
                  >
                    <TableCell padding="none" align="center">
                      {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDate(first.created_ts)}>
                        <span>{formatRelativeTime(first.created_ts)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={`Edit event · ${group.rows.length} records`} />
                    </TableCell>
                    <TableCell>{first.actor?.email ?? <em>system</em>}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={[first.ip, first.user_agent].filter(Boolean).join(' · ')}>
                        <span>{SOURCE_LABEL[first.source] ?? first.source}</span>
                      </Tooltip>
                      <ChangeSetChip changeSet={changeSet} sx={{ ml: 0.5 }} />
                    </TableCell>
                    <TableCell>
                      {objects.slice(0, MAX_EVENT_CHIPS).map((label) => (
                        <Chip
                          key={label}
                          size="small"
                          variant="outlined"
                          label={label}
                          sx={{ mr: 0.5, mb: 0.25 }}
                        />
                      ))}
                      {objects.length > MAX_EVENT_CHIPS && (
                        <Chip size="small" label={`+${objects.length - MAX_EVENT_CHIPS}`} />
                      )}
                    </TableCell>
                    {also && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }} data-history-root>
                        {Array.from(new Set(group.rows.map((r) => labelOf(r)))).join(' · ')}
                      </TableCell>
                    )}
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" noWrap title={first.comment}>
                        {first.comment || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {open &&
                    group.rows.map((row) => (
                      <ChangeRow
                        key={row.id}
                        row={row}
                        expanded={expanded === row.id}
                        onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                        onRestored={restored}
                        nested
                        rootLabel={labelOf(row)}
                      />
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <HelpLink story="ui-change-history" newTab />
        <TablePagination
          component="div"
          sx={{ flexGrow: 1 }}
          count={data.count ?? -1}
          page={page}
          onPageChange={(_, p) => {
            setExpanded(null);
            setOpenGroups(new Set());
            setPage(p);
          }}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Box>
    </Box>
  );
}
